import React, { useEffect } from 'react';
import Seo from './Seo';
import { INDEX_SEO } from './services/serviceData';
import { Icon, Mark, Flow, MarkStrip, CapGrid } from './services/ServiceFlow';

/* Customer-facing. Only marks a customer would recognise and care about
   are shown: where their outreach is sent, how their guests can pay, and
   which platforms we ship to. Internal vendors are never surfaced. */

const marks = {
  gmail:     <Mark src="/brand/gmail.svg"            alt="Gmail" />,
  stripe:    <Mark src="/brand/stripe.svg"           alt="Stripe"      wide />,
  square:    <Mark src="/brand/square.svg"           alt="Square"      light />,
  applePay:  <Mark src="/brand/apple-pay.svg"        alt="Apple Pay"   light wide />,
  googlePay: <Mark src="/brand/google-pay.svg"       alt="Google Pay"  light wide />,
  apple:     <Mark src="/brand/apple-logo.svg"       alt="Apple" />,
  play:      <Mark src="/brand/google-play-icon.svg" alt="Google Play" />,
};

const Services = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="sv-page">
      <Seo {...INDEX_SEO} />

      {/* ── Hero ── */}
      <header className="sv-hero">
        <div className="sv-shell">
          <span className="sv-eyebrow">Services</span>
          <h1 className="sv-title">What we build &amp; manage</h1>
        </div>
      </header>

      {/* ══ 01 · AUTOMATION ═══════════════════════════════════════ */}
      <section className="sv-block">
        <div className="sv-shell">
          <div className="sv-block-head">
            <span className="sv-idx">01</span>
            <h2 className="sv-h2">Automation</h2>
            <p className="sv-lead">
              Destiny is an autonomous operations platform built from three engines:
              one that finds and pitches leads, one that runs the phones, and one that
              handles guests in person. Each runs unattended, around the clock, and
              reports back to a single dashboard.
            </p>
          </div>

          <h3 className="sv-h3">Leads engine</h3>
          <p className="sv-sub">
            Give it an industry and a city. It surfaces every matching business,
            researches each one, and writes and sends a pitch built around whatever
            you're actually offering, not a fixed script. You wake up to replies.
          </p>

          <Flow stage="Find and qualify" steps={[
            { label: 'Target',      icon: <Icon.Search /> },
            { label: 'Grid Scrape', icon: <Icon.Grid /> },
            { label: 'Find Email',  icon: <Icon.Mail /> },
            { label: 'Score',       icon: <Icon.Star /> },
          ]} />
          <Flow stage="Pitch and deliver" steps={[
            { label: 'Write Pitch', icon: <Icon.Pen /> },
            { label: 'Send',        icon: marks.gmail },
            { label: 'Report',      icon: <Icon.Doc /> },
          ]} />

          <CapGrid items={[
            { icon: <Icon.Grid />,   title: 'Complete map coverage',    desc: 'Tiles a whole city into overlapping cells so no matching business is missed.' },
            { icon: <Icon.Mail />,   title: 'Owner email discovery',    desc: 'Finds the decision-maker’s address automatically, not a generic inbox.' },
            { icon: <Icon.Pen />,    title: 'Review-aware pitches',     desc: 'Reads each lead’s real reviews and rating, then writes a message that sounds researched, not templated.' },
            { icon: <Icon.Rocket />, title: 'Optional: a site, built in', desc: 'When it strengthens the pitch, Destiny can hand a lead a working site of their own, built from their real photos and reviews. Not required, and never the point of the outreach.' },
            { icon: <Icon.Timer />,  title: 'Self-expiring previews',   desc: 'Preview sites delete themselves after 14 days. Converts keep theirs.' },
            { icon: <Icon.Doc />,    title: 'Lead intelligence reports', desc: 'Contacts, ratings, review sentiment, and opportunities, exported as a polished PDF.' },
            { icon: <Icon.Eye />,    title: 'Competitor intel',         desc: 'Profiles the competition around each lead so the pitch lands on a real gap.' },
            { icon: <Icon.Phone />,  title: 'Voice and SMS fallback',   desc: 'No email found? The lead is handed to the phone engine automatically.' },
          ]} />

          <h3 className="sv-h3 sv-h3--gap">Phone engine</h3>
          <p className="sv-sub">
            An AI front desk. Inbound calls are answered instantly with a natural HD
            voice; outbound campaigns dial your list, detect voicemails, and fall back
            to SMS. Every call is recorded and summarised.
          </p>

          <Flow stage="Inbound" steps={[
            { label: 'Call In',      icon: <Icon.Phone /> },
            { label: 'Greet',        icon: <Icon.Wave /> },
            { label: 'Collect',      icon: <Icon.Keypad /> },
            { label: 'Take Action',  icon: <Icon.Hook /> },
            { label: 'Transfer',     icon: <Icon.Transfer /> },
            { label: 'Follow Up',    icon: <Icon.Chat /> },
          ]} />
          <Flow stage="Outbound" steps={[
            { label: 'Upload List',  icon: <Icon.Upload /> },
            { label: 'Dial',         icon: <Icon.Phone /> },
            { label: 'Voicemail?',   icon: <Icon.Tape /> },
            { label: 'SMS Fallback', icon: <Icon.Chat /> },
            { label: 'Escalate',     icon: <Icon.Transfer /> },
          ]} />

          <CapGrid items={[
            { icon: <Icon.Wave />,     title: 'Natural HD voice',          desc: 'A clear, human-sounding voice on every call, inbound or outbound.' },
            { icon: <Icon.Keypad />,   title: 'Speech or keypad input',    desc: 'Callers answer by voice or keypad; the conversation branches on either.' },
            { icon: <Icon.Hook />,     title: 'Actions mid-call',          desc: 'Book, ticket, order, or look up a status in your own systems while the caller waits.' },
            { icon: <Icon.Transfer />, title: 'Warm, blind, conference',   desc: 'Hands off to a live agent with full context, any transfer style.' },
            { icon: <Icon.Tape />,     title: 'Voicemail detection',       desc: 'Knows when a machine picks up and follows up by SMS instead.' },
            { icon: <Icon.Record />,   title: 'Recording and summaries',   desc: 'Every call recorded, with the path it took summarised on your dashboard.' },
          ]} />
        </div>
      </section>

      {/* ══ 02 · IN-PERSON ENGAGEMENT ═════════════════════════════ */}
      <section className="sv-block">
        <div className="sv-shell">
          <div className="sv-block-head">
            <span className="sv-idx">02</span>
            <h2 className="sv-h2">In-Person Engagement</h2>
            <p className="sv-lead">
              Replace the clipboard, the line, and the register. Guests scan a code,
              join a live queue, get served in order, and pay on the spot. Runs on a
              tablet in kiosk mode or on their own phone.
            </p>
          </div>

          <Flow steps={[
            { label: 'Guest Scans', icon: <Icon.Qr /> },
            { label: 'Joins Queue', icon: <Icon.Queue /> },
            { label: 'Served',      icon: <Icon.Tablet /> },
            { label: 'Pays',        icon: <Icon.Pos /> },
          ]} />

          <MarkStrip caption="Payments and hardware" marks={[
            { node: marks.stripe,    alt: 'Stripe' },
            { node: marks.square,    alt: 'Square' },
            { node: marks.applePay,  alt: 'Apple Pay' },
            { node: marks.googlePay, alt: 'Google Pay' },
            { node: <span className="sv-mark"><Icon.Tablet /></span>, alt: 'Tablet kiosk', label: 'Tablet Kiosk' },
            { node: <span className="sv-mark"><Icon.Pos /></span>,    alt: 'Point of sale', label: 'Point of Sale' },
          ]} />
        </div>
      </section>

      {/* ══ 03 · WEB & APP DEVELOPMENT ════════════════════════════ */}
      <section className="sv-block">
        <div className="sv-shell">
          <div className="sv-block-head">
            <span className="sv-idx">03</span>
            <h2 className="sv-h2">Web &amp; App Development</h2>
            <p className="sv-lead">
              Web platforms and mobile applications engineered end-to-end: architecture,
              infrastructure, and interface. We build for the browser and for both
              app stores, host it, and keep it running after launch.
            </p>
          </div>

          <MarkStrip caption="Platforms we build for" marks={[
            { node: <span className="sv-mark"><Icon.Globe /></span>, alt: 'Web' },
            { node: marks.apple, alt: 'iOS' },
            { node: marks.play,  alt: 'Android' },
          ]} />

          <CapGrid items={[
            { icon: <Icon.Layers />, title: 'Custom software',      desc: 'Internal tools and business systems engineered around how you actually work.' },
            { icon: <Icon.Code />,   title: 'SaaS platforms',       desc: 'Multi-tenant products with billing, auth, and dashboards, built to scale.' },
            { icon: <Icon.Mobile />, title: 'Mobile apps',          desc: 'Native-quality iOS and Android from a single roadmap.' },
            { icon: <Icon.Globe />,  title: 'Website development',  desc: 'Fast, responsive marketing sites and web apps with accessible front-ends.' },
            { icon: <Icon.Cart />,   title: 'E-commerce',           desc: 'Storefronts, checkout, and payment flows tuned for conversion.' },
            { icon: <Icon.Server />, title: 'Web hosting',          desc: 'Managed hosting and deployment so your software stays online and patched.' },
            { icon: <Icon.Cloud />,  title: 'Cloud infrastructure', desc: 'Sized to the workload, secured, and built to grow with you.' },
            { icon: <Icon.Shield />, title: 'Backup and recovery',  desc: 'Automated backups and recovery plans that keep your data restorable.' },
          ]} />
        </div>
      </section>

      <section className="sv-cta">
        <div className="sv-shell">
          <h2 className="sv-h2">Start with a conversation.</h2>
          <a className="lp-btn lp-btn-primary" href="mailto:info@tavernix.io">info@tavernix.io</a>
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-shell lp-footer-inner">
          <span>&copy; 2026 Tavernix Systems // Est_2024</span>
          <a href="mailto:info@tavernix.io">info@tavernix.io</a>
        </div>
      </footer>
    </div>
  );
};

export default Services;
